import { useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import {
  birthDateIssueMessage,
  type BirthDateCopy,
} from "@shared/birthDateCopy";
import {
  emptyBirthDateParts,
  formatBirthDateLong,
  partsFromIso,
  displayMonth,
  readBirthDateEntry,
  resolveBirthDate,
  type BirthDateField,
  type BirthDateIssue,
  type BirthDateParts,
} from "@shared/birthDateParts";

type Variant = "booking" | "admin";

type Props = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  onErrorChange?: (message: string | null) => void;
  copy: BirthDateCopy;
  locale: "de" | "en";
  variant?: Variant;
  legend?: string;
  required?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
};

function joinClasses(parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

function fieldRefs(
  day: HTMLInputElement | null,
  month: HTMLInputElement | null,
  year: HTMLInputElement | null,
): Record<BirthDateField, HTMLInputElement | null> {
  return { day, month, year };
}

export function BirthDateInput({
  value,
  onChange,
  onBlur,
  onErrorChange,
  copy,
  locale,
  variant = "booking",
  legend,
  required = false,
  disabled = false,
  invalid = false,
  describedBy,
}: Props) {
  const prefix = variant === "admin" ? "eh-birthdate" : "bk-birthdate";
  const inputClass = variant === "admin" ? "eh-input" : "bk-input";
  const hintId = useId();
  const feedbackId = useId();
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const emittedRef = useRef(value);
  const errorRef = useRef<string | null>(null);
  const partsRef = useRef<BirthDateParts>(partsFromIso(value));
  const advancingRef = useRef(false);
  const advanceTimerRef = useRef<number | null>(null);
  const [parts, setParts] = useState<BirthDateParts>(() => partsFromIso(value));
  const [issue, setIssue] = useState<BirthDateIssue | null>(null);

  function inputs(): Record<BirthDateField, HTMLInputElement | null> {
    return fieldRefs(dayRef.current, monthRef.current, yearRef.current);
  }

  function syncValidity(message: string | null, nextIssue: BirthDateIssue | null, nextParts: BirthDateParts) {
    const nodes = inputs();
    nodes.day?.setCustomValidity("");
    nodes.month?.setCustomValidity("");
    nodes.year?.setCustomValidity("");
    if (!message) return;
    const target =
      nextIssue === "yearLength"
        ? nodes.year
        : nextIssue === "incomplete"
          ? !nextParts.day
            ? nodes.day
            : !nextParts.month
              ? nodes.month
              : nodes.year
          : nodes.year ?? nodes.day;
    target?.setCustomValidity(message);
  }

  function clearAdvanceTimer() {
    if (advanceTimerRef.current === null) return;
    window.clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = null;
  }

  function scheduleAmbiguousAdvance(field: BirthDateField, valuePart: string) {
    clearAdvanceTimer();
    // Nur der Tag wartet kurz bei 1–3. Der Monat bleibt bei 0 und 1 stehen, bis MM vollständig ist.
    const ambiguousDay = field === "day" && /^\d$/.test(valuePart) && Number(valuePart) <= 3;
    if (!ambiguousDay) return;
    if (partsRef.current.month !== "") return;
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null;
      if (partsRef.current.day !== valuePart) return;
      if (partsRef.current.month !== "") return;
      focusField("month");
    }, 800);
  }

  function focusField(field: BirthDateField | null) {
    clearAdvanceTimer();
    if (!field) return;
    const node = inputs()[field];
    if (!node || document.activeElement === node) return;
    advancingRef.current = true;
    node.focus();
  }

  function commit(nextParts: BirthDateParts, finalize: boolean) {
    const resolved = resolveBirthDate(nextParts, { finalize });
    partsRef.current = resolved.parts;
    setParts(resolved.parts);
    setIssue(resolved.issue);
    const message = birthDateIssueMessage(resolved.issue, copy);
    syncValidity(message, resolved.issue, resolved.parts);
    if (message !== errorRef.current) {
      errorRef.current = message;
      onErrorChange?.(message);
    }
    const previousIso = emittedRef.current;
    if (resolved.iso !== previousIso) {
      emittedRef.current = resolved.iso;
      onChange(resolved.iso);
    }
    if (finalize || (resolved.iso !== "" && resolved.iso !== previousIso)) {
      onBlur?.(resolved.iso);
    }
    return resolved;
  }

  useEffect(() => () => clearAdvanceTimer(), []);

  useEffect(() => {
    if (value === emittedRef.current) return;
    emittedRef.current = value;
    const next = value ? partsFromIso(value) : emptyBirthDateParts();
    partsRef.current = next;
    setParts(next);
    setIssue(null);
    errorRef.current = null;
    onErrorChange?.(null);
    syncValidity(null, null, next);
    // onErrorChange is only used to clear a stale parent error when the value is replaced from outside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    syncValidity(birthDateIssueMessage(issue, copy), issue, partsRef.current);
  }, [issue, copy]);

  function handleChange(field: BirthDateField, raw: string) {
    clearAdvanceTimer();
    const entry = readBirthDateEntry(field, raw, partsRef.current);
    commit(entry.parts, entry.finalize);
    if (entry.focus && entry.focus !== field) {
      focusField(entry.focus);
      return;
    }
    if (!entry.finalize) {
      scheduleAmbiguousAdvance(field, entry.parts[field]);
    }
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    const next = event.relatedTarget;
    const root = event.currentTarget.closest("fieldset");
    if (next instanceof Node && root?.contains(next)) return;
    clearAdvanceTimer();
    commit(partsRef.current, true);
  }

  function handleKeyDown(field: BirthDateField, event: KeyboardEvent<HTMLInputElement>) {
    const separator = event.key === "." || event.key === "/" || event.key === " " || event.key === "-";
    if (separator) {
      event.preventDefault();
      if (field !== "year" && event.currentTarget.value.length > 0) {
        if (field === "month") {
          const month = displayMonth(partsRef.current.month, true);
          if (month !== partsRef.current.month) {
            commit({ ...partsRef.current, month }, false);
          }
        }
        const following = field === "day" ? "month" : "year";
        focusField(following);
      }
      return;
    }
    if (event.key === "Backspace" && event.currentTarget.value.length === 0) {
      const previous = field === "year" ? "month" : field === "month" ? "day" : null;
      if (!previous) return;
      event.preventDefault();
      focusField(previous);
    }
  }

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    if (advancingRef.current) {
      advancingRef.current = false;
      return;
    }
    event.currentTarget.select();
  }

  const resolvedNow = resolveBirthDate(parts);
  const confirmation =
    issue === null && resolvedNow.iso ? formatBirthDateLong(resolvedNow.iso, locale) : null;
  const message = birthDateIssueMessage(issue, copy);
  const anyFilled = Boolean(parts.day || parts.month || parts.year);
  const fieldRequired = required || anyFilled;
  const showInvalid = invalid || Boolean(issue);
  const described = [hintId, message || confirmation ? feedbackId : null, describedBy]
    .filter(Boolean)
    .join(" ");

  function renderField(field: BirthDateField, autoComplete: string, placeholder: string, label: string) {
    return (
      <label className={`${prefix}__part`} key={field}>
        {label}
        <input
          ref={field === "day" ? dayRef : field === "month" ? monthRef : yearRef}
          className={joinClasses([
            inputClass,
            `${prefix}__input`,
            showInvalid && `${prefix}__input--invalid`,
          ])}
          inputMode="numeric"
          autoComplete={autoComplete}
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint={field === "year" ? "done" : "next"}
          placeholder={placeholder}
          disabled={disabled}
          required={fieldRequired}
          aria-invalid={showInvalid || undefined}
          aria-describedby={described || undefined}
          value={parts[field]}
          onChange={(event) => handleChange(field, event.target.value)}
          onKeyDown={(event) => handleKeyDown(field, event)}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </label>
    );
  }

  return (
    <fieldset className={prefix} disabled={disabled} data-testid="birth-date">
      {legend ? <legend className={`${prefix}__legend`}>{legend}</legend> : null}
      <div className={`${prefix}__grid`}>
        {renderField("day", "bday-day", copy.dayPlaceholder, copy.day)}
        {renderField("month", "bday-month", copy.monthPlaceholder, copy.month)}
        {renderField("year", "bday-year", copy.yearPlaceholder, copy.year)}
      </div>
      <p id={hintId} className={`${prefix}__hint`}>
        {copy.hint}
      </p>
      {message ? (
        <p id={feedbackId} className={`${prefix}__error`} role="alert">
          {message}
        </p>
      ) : confirmation ? (
        <p
          id={feedbackId}
          className={joinClasses([
            `${prefix}__confirm`,
            invalid && `${prefix}__confirm--neutral`,
          ])}
          role="status"
        >
          {copy.confirmed(confirmation)}
        </p>
      ) : null}
    </fieldset>
  );
}
